import { Injectable } from '@angular/core';
import {AlertController, LoadingController, Platform} from '@ionic/angular';
import {AppUpdate} from '@ionic-native/app-update/ngx';
import {AppVersion} from '@ionic-native/app-version/ngx';
import { ApiBasicService } from '../api/api-basic.service';
import { NetworkService } from '../network.service';
import { ScannerApiService } from '../scanner/scanner-api.service';
import { PipelineStageConfig, StatusObject } from '../../interfaces/biozoom-api/pipeline';
import {Observable, of, OperatorFunction} from 'rxjs';
import { ScanGenericService } from '../scanGeneric/scan-generic.service';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import { State } from '../../interfaces/biozoom-api/app-configuration';
import {switchMap, tap} from 'rxjs/operators';
import { BarcodeScanner } from '@awesome-cordova-plugins/barcode-scanner/ngx';
import {
  fillString, fillURL, getKeyVal, setKeyVal, getScannerSoftwareVersionBySerialNumber, getScannerSoftwareVersionByWifiFirmware,
  getScannerSCNModelFromSerialNumber, getApiUrl
} from '../../methods/helpers';
import { DownloadService } from '../fileManager/download.service';
import { Storage } from '@ionic/storage';
import { environment } from '../../../environments/environment';
import { getResult } from '../../methods/result';
import { AppConfigService } from '../app-config.service';
import {AoxMeasurementResult} from '../../interfaces/biozoom-api/aox-measurement-result';
import {PreciseAoxMeasurementResult} from '../../interfaces/biozoom-api/precise-aox-measurement-result';
import {validateMeasurementAllowance} from '../../methods/measurement';
import {MeasurementType} from '../../interfaces/biozoom-api/license';

const APP_UPDATE_MANIFEST_TIMEOUT_MS = 30_000;

@Injectable({
  providedIn: 'root'
})
export class GenericStageService {

  constructor(
    private barcodeScanner: BarcodeScanner,
    private scanGenericService: ScanGenericService,
    private apiBasicService: ApiBasicService,
    private networkService: NetworkService,
    private scannerApiService: ScannerApiService,
    private downloadService: DownloadService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private appUpdate: AppUpdate,
    private appVersion: AppVersion,
    private platform: Platform,
    private appConfigService?: AppConfigService,
  ) {
  }

  /**
   * fetch the latest config object for the registered user
   * if it differs from the currently loaded config, fetch all new media
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  refresh = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      if (pipelineConfig.skipRefresh?.()) {
        return of(true);
      }
      await pipelineConfig.fetchAppConfig();
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * if available, download the latest version of the application, and install it
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  updateApp = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      const requestedAppName = pipelineConfig.stageConfig.appName;
      const scannerProduct = pipelineConfig.state?.persistentValues?.SCANNER_STATUS?.product;
      const appName = requestedAppName || scannerProduct || this.apiBasicService.getConfiguration().appName;
      const mandatory = pipelineConfig.stageConfig.mandatory === true;
      const beta = pipelineConfig.stageConfig.beta === true;

      const canUseNativeAndroidUpdate = this.isSupportedApp(appName)
        && this.platform.is('cordova')
        && this.platform.is('android');

      if (!canUseNativeAndroidUpdate) {
        pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }

      try {
        const updateUrl = `${getApiUrl()}/app/update/latest/${encodeURIComponent(appName)}${beta ? '?beta=true' : ''}`;
        const checkingLoading = await this.loadingController.create({
          message: 'Checking for update…',
          spinner: 'crescent',
          backdropDismiss: false,
        });
        await checkingLoading.present();
        let targetVersion: string | undefined;
        try {
          const manifestResponse = await this.withTimeout(
            fetch(updateUrl, {method: 'GET'}),
            APP_UPDATE_MANIFEST_TIMEOUT_MS,
            'App update manifest check timed out',
          );
          if (manifestResponse.status === 404) {
            this.executeMissingApkStage(pipelineConfig);
            return of(true);
          }
          if (!manifestResponse.ok) {
            throw new Error(`App update manifest request failed with status ${manifestResponse.status}`);
          }

          targetVersion = this.getManifestVersion(await manifestResponse.text());
        } finally {
          try {
            await checkingLoading.dismiss();
          } catch (error) {
            console.warn('Unable to dismiss app update checking overlay', error);
          }
        }

        if (targetVersion && this.compareVersions(await this.appVersion.getVersionNumber(), targetVersion) >= 0) {
          pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return of(true);
        }

        if (!await this.confirmUpdate(targetVersion, mandatory, beta)) {
          pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onSkip, 'onSkip', pipelineConfig.stage);
          return of(true);
        }

        const downloadLoading = await this.loadingController.create({
          message: 'Downloading update…',
          spinner: 'crescent',
          backdropDismiss: false,
        });
        await downloadLoading.present();
        try {
          const updateResult = await this.appUpdate.checkAppUpdate(updateUrl, {
            skipPromptDialog: true,
            skipProgressDialog: true,
            authType: 'basic',
            password: this.apiBasicService.getConfiguration().upgradePassword,
            username: this.apiBasicService.getConfiguration().upgradeUser,
          });
          if (this.isMissingApkResult(updateResult)) {
            this.executeMissingApkStage(pipelineConfig);
            return of(true);
          }
          // The plugin resolves as soon as the Android download dialog is open.
          // Continue the app pipeline so cancelling the later installer cannot
          // leave this stage's loading state active.
          pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        } finally {
          try {
            await downloadLoading.dismiss();
          } catch (error) {
            console.warn('Unable to dismiss app update download overlay', error);
          }
        }
        // Android restarts the process after a successful installation. The updated
        // app continues via its normal initial route, not this pipeline's onSuccess.
      } catch (error) {
        console.error('App update failed', error);
        if (this.isMissingApkResult(error)) {
          this.executeMissingApkStage(pipelineConfig);
        } else {
          pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        }
      }
      return of(true);
    });
  }

  private async confirmUpdate(targetVersion: string | undefined, mandatory: boolean, beta: boolean): Promise<boolean> {
    const alert = await this.alertController.create({
      header: 'UPDATE',
      message: `${beta ? 'A beta update' : `Version ${targetVersion ?? 'unknown'}`} is available.${mandatory ? ' This update is required to continue.' : ''}`,
      backdropDismiss: false,
      buttons: mandatory
        ? [{text: 'Update', role: 'confirm'}]
        : [{text: 'Skip', role: 'cancel'}, {text: 'Update', role: 'confirm'}],
    });
    await alert.present();
    return (await alert.onDidDismiss()).role === 'confirm';
  }

  private isSupportedApp(appName: string): boolean {
    return appName === 'VitalityCheck' || appName === 'VEGEcheck';
  }

  private getManifestVersion(manifest: string): string | undefined {
    return manifest.match(/<appVersion>(\d+\.\d+\.\d+)<\/appVersion>/)?.[1];
  }

  private compareVersions(left: string, right: string): number {
    const leftParts = left.split('.').map(part => Number(part));
    const rightParts = right.split('.').map(part => Number(part));
    for (let index = 0; index < 3; index++) {
      const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0);
      if (difference !== 0) {
        return difference;
      }
    }
    return 0;
  }

  private executeMissingApkStage(pipelineConfig: PipelineStageConfig): void {
    const missingApkTarget = pipelineConfig.stageConfig.eventTargets?.onMissingApk;
    if (missingApkTarget) {
      pipelineConfig.executeStage(missingApkTarget, 'onMissingApk', pipelineConfig.stage);
    } else {
      pipelineConfig.executeStage(pipelineConfig.stageConfig.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
    }
  }

  private isMissingApkResult(result: unknown): boolean {
    const code = this.getAppUpdateResultCode(result);
    return code === 404;
  }

  private getAppUpdateResultCode(result: unknown): number | undefined {
    if (typeof result === 'object' && result !== null && 'code' in result) {
      const code = Number((result as {code: unknown}).code);
      return Number.isFinite(code) ? code : undefined;
    }
    if (typeof result === 'string') {
      try {
        return this.getAppUpdateResultCode(JSON.parse(result));
      } catch (_) {
        return undefined;
      }
    }
    return undefined;
  }

  private withTimeout<T>(operation: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = Number.isFinite(timeoutMs)
        ? setTimeout(() => reject(new Error(message)), timeoutMs)
        : undefined;
      operation.then(
        result => {
          if (timer !== undefined) {
            clearTimeout(timer);
          }
          resolve(result);
        },
        error => {
          if (timer !== undefined) {
            clearTimeout(timer);
          }
          reject(error);
        },
      );
    });
  }

  writeLog = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      const messageTemplate = pipelineConfig.stageConfig.logParameters?.message;
      if (!messageTemplate?.trim()) {
        pipelineConfig.executeStage(
          pipelineConfig.stageConfig.eventTargets?.onFail,
          'onFail',
          pipelineConfig.stage
        );
        return of(true);
      }

      const nextStage = pipelineConfig.stageConfig.eventTargets?.onSuccess;
      await pipelineConfig.pushToLog({
        currentStage: pipelineConfig.stage,
        nextStage,
        timestamp: Math.floor(Date.now() / 1000),
        msg: fillString(messageTemplate, pipelineConfig.state),
        type: 'msg'
      });
      pipelineConfig.executeStage(nextStage, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }


  get = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      // pipelineConfig.setBlockScreen(true);
      try {
        // const body = pipelineConfig.state?.postParameters ?? {};
        let url = pipelineConfig.stageConfig.getURL ?? '';
        const getKey = pipelineConfig.stageConfig.getKey ?? '';
        const getGroup = pipelineConfig.stageConfig.getGroup ?? '';
        const state = pipelineConfig.state;
        if (url === '' || getKey === '' || getGroup === '') {
          // pipelineConfig.setBlockScreen(false);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingURL, 'onMissingURL', pipelineConfig.stage);
          return of(true);
        }
        url = fillURL(url, state);
        const resp = await fetch(url, {
          method: 'GET',
        });
        if (resp.status !== 200) {
          // pipelineConfig.setBlockScreen(false);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
          return of(true);
        }
        const result = await resp.json();
        pipelineConfig.setState(getKey, result, getGroup);

        // pipelineConfig.createState('getResult', result);
        // pipelineConfig.setBlockScreen(false);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      } catch (e) {
        // pipelineConfig.setBlockScreen(false);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
    });
  }

  /**
   * perform a POST-request to a given url with a given object of values, used as the body
   * the post-body is a sub-store of the value store, accessed by the key 'postParameters'
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  post = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      // pipelineConfig.setBlockScreen(true);
      const body = pipelineConfig.state?.postParameters ?? {};
      let url = pipelineConfig.stageConfig.postURL ?? '';
      if (url === '') {
        // pipelineConfig.setBlockScreen(false);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingURL, 'onMissingURL', pipelineConfig.stage);
        return of(true);
      }
      url = fillURL(url, pipelineConfig.state);
      const postKey = pipelineConfig.stageConfig.postKey ?? '';
      const postGroup = pipelineConfig.stageConfig.postGroup ?? '';

      let resp;

      try {
        const fileElement: HTMLInputElement = document.getElementById('upload-file') as HTMLInputElement;
        const file = fileElement.files[0];
        const formData = new FormData();
        const jwt = pipelineConfig.state.persistentValues.JWT;
        if (!jwt) {
          throw new Error('');
        }
        formData.append('file', file);

        resp = await fetch(url, {
          method: 'POST',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/json; charset=UTF-8',
            Authorization: 'Bearer ' + jwt,
          },
          body: formData
        });
      } catch (e) {
        const headers: Record<string, string> = {
          'Access-Control-Allow-Origin': '*',
          Accept: 'application/json; charset=UTF-8',
          'Content-Type': 'application/json',
        };

        const jwt = pipelineConfig.state.persistentValues.JWT;
        if (jwt) {
          headers.Authorization = 'Bearer ' + jwt;
        }
        resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        });
      }



      pipelineConfig.clearState('postParameters');
      if (resp.status !== 200) {
        // pipelineConfig.setBlockScreen(false);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
      try {
        const data = await resp.json();
        if (!([undefined, null].includes(data))) {
          if (Object.keys(data).includes('access_token')) {
            pipelineConfig.setState(State.JWT, data.access_token);
          }
          pipelineConfig.setState(postKey, data, postGroup);
        }
      } catch (e) {
      }

      // pipelineConfig.setBlockScreen(false);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * perform a GET-request to download a single image/video, based on a target url and an image-key
   * the key is used to access an image that is part of an image-directory
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  download = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      const state = pipelineConfig.state;
      const url = pipelineConfig.stageConfig.getURL ?? '';
      let key = pipelineConfig.stageConfig.getKey ?? '';
      if (key === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingURL, 'onMissingURL', pipelineConfig.stage);
        return of(true);
      }
      // replace variable-placeholder within the key with the actual values, if possible
      key = fillURL(key, state);

      if (!(environment.media)) {
        environment.media = {};
      }

      const urlObj = await this.downloadService.getURLOne(url, key);
      if (!urlObj) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingURL, 'onMissingURL', pipelineConfig.stage);
        return of(true);
      }
      // final url of the mapped image
      const urlMapped = urlObj.alternateName ?? '';

      // url of the base image combined with the image-key
      const urlParam = `${url}_${key}`;

      // if an image is stored under the urlParam-key, AND if the url it was fetched from
      // is equal to the urlMapped variable, load and use the local image instead
      const store = new Storage({}, 'localstorage');
      const imageOldLoaded = await store.get(`${urlParam}_0`);
      const imageOldOrigin = await store.get(`${urlParam}_origin`);
      if (imageOldLoaded && imageOldOrigin === urlMapped) {
        const resParts: string[] = [imageOldLoaded];
        let i = 1;
        while (true) {
          const imagePart = await store.get(`${urlParam}_${i}`);
          if (!imagePart) {
            break;
          }
          resParts.push(imagePart);
          i++;
        }
        environment.media[url] = resParts.join('');
        await store.set(url, resParts.join(''));
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }


      await this.downloadService.removeKey(url, urlParam, store);

      const urls = urlObj.loc.map((host) => `https://${host}/${urlMapped}`);
      for (const u of urls) {
        const imagesEncoded: string[] | undefined = await this.downloadService.downloadOne(u);
        if (!imagesEncoded) {
          continue;
        }
        for (let i = 0; i < imagesEncoded.length; i++) {
          const image = imagesEncoded[i];
          await store.set(`${url}_${i}`, image);
          await store.set(`${urlParam}_${i}`, image);
        }
        environment.media[url] = imagesEncoded.join('');
        await store.set(`${urlParam}_origin`, urlMapped);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
      return of(true);
    });
  }

  getConfig = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      const getKey = pipelineConfig.stageConfig.resKey ?? '';
      const getGroup = pipelineConfig.stageConfig.resGroup ?? '';
      const reset = pipelineConfig.stageConfig.resetConfig ?? false;
      const configName = getKeyVal(getKey, pipelineConfig.state?.[getGroup] ?? {});
      const didFetch = await this.appConfigService?.load(configName, reset);

      if (didFetch) {
        await pipelineConfig.fetchAppConfig(true);
        // await this.router.navigateByUrl('/download');
        // pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
      }
    });
  }

  logout = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      pipelineConfig.setState(State.USERNAME, undefined);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  splitString = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const splitParams = pipelineConfig.stageConfig?.splitParameters;
      if (splitParams) {
        if (!pipelineConfig.state[splitParams.targetGroup]) {
          pipelineConfig.state[splitParams.targetGroup] = {};
        }
        const newValue =
          ((pipelineConfig.state?.[splitParams.originGroup] ?? {})?.[splitParams.originKey] ?? '').split(splitParams.splitCharacter);

        pipelineConfig.setState(splitParams.targetKey, newValue, splitParams.targetGroup);
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * Set values within the value storage object
   * Requires an input value, as well as a target group- and key for each operation
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  setValues = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const setParams = pipelineConfig.stageConfig?.setParameters;
      if (setParams) {
        for (const setParam of setParams) {
          if (!pipelineConfig.state[setParam.targetGroup]) {
            pipelineConfig.state[setParam.targetGroup] = {};
          }
          let newValue = setParam.newValue;
          if (setParam.newValueType === 'function') {
            switch (newValue) {
              case 'add 1': {
                // take a variable (or initialize it with 0), then add 1 to it
                // let oldValue = pipelineConfig.state[setParam.targetGroup][setParam.targetKey];
                let oldValue = getKeyVal(setParam.targetKey, pipelineConfig.state?.[setParam.targetGroup] ?? {});
                oldValue = Number(oldValue);
                if (isNaN(oldValue)) {
                  newValue = 1;
                } else {
                  newValue = oldValue + 1;
                }
                break;
              }
              case 'subtract 1': {
                // take a variable (or initialize it with 0), then subtract 1 from it
                // let oldValue = pipelineConfig.state[setParam.targetGroup][setParam.targetKey];
                let oldValue = getKeyVal(setParam.targetKey, pipelineConfig.state?.[setParam.targetGroup] ?? {});
                oldValue = Number(oldValue);
                if (isNaN(oldValue)) {
                  newValue = -1;
                } else {
                  newValue = oldValue - 1;
                }
                break;
              }
              case '+': {
                // take two variables (or initialize them with 0), then add them together
                // const val1 = Number(pipelineConfig.state[setParam.inputGroup1]?.[setParam.inputKey1] ?? 0);
                // const val2 = Number(pipelineConfig.state[setParam.inputGroup2]?.[setParam.inputKey2] ?? 0);
                const val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? 0;
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? 0;
                newValue = val1 + val2;
                break;
              }
              case '-': {
                // take two variables (or initialize them with 0), then subtract the second from the first
                const val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? 0;
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? 0;
                newValue = val1 - val2;
                break;
              }
              case '*': {
                // take two variables (or initialize them with 0), then multiple them together
                const val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? 0;
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? 0;
                newValue = val1 * val2;
                break;
              }
              case '/': {
                // take two variables (or initialize them with 0), then divide the first value by the second
                const val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? 0;
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? 0;
                newValue = val1 / val2;
                break;
              }
              case 'diff': {
                // take two variables (or initialize them with 0), then build the absolute difference
                const val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {});
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {});
                if ([null, undefined].includes(val1) || [null, undefined].includes(val2)) {
                  newValue = 0;
                } else {
                  newValue = Math.abs(val1 - val2);
                }
                break;
              }
              case 'random': {
                // generate a pseudo-random value within two given limits
                const min = Number(setParam.min) ?? 1;
                const max = Number(setParam.max) ?? 1;
                newValue = Math.random();
                if ((setParam.isInt === undefined || setParam.isInt === null) || setParam.isInt) {
                  // in order to allow for the max-value to be included in the integer-generation,
                  // the maximum has to be risen by 1
                  // e.g. without this adjustment, a random value between 0 and 4 would be
                  // (0 - 0.9999) -> (0 - 3.99999) -> {0, 1, 2, 3}
                  // the value '4' could never be returned, to the floor function
                  // adding '1' to the multiplier solves this
                  newValue = (newValue * (max - min + 1)) + min;
                  newValue = Math.floor(newValue);
                } else {
                  newValue = (newValue * (max - min)) + min;
                }
                break;
              }
              case 'round': {
                const accuracy = setParam.accuracy ?? 0;
                newValue = Number(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}));
                newValue = Number((Math.round((newValue ?? 0) * Math.pow(10, accuracy)) / Math.pow(10, accuracy)).toFixed(accuracy));
                break;
              }
              case 'ceil': {
                const accuracy = setParam.accuracy ?? 0;
                newValue = Number(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}));
                newValue = Number((Math.ceil((newValue ?? 0) * Math.pow(10, accuracy)) / Math.pow(10, accuracy)).toFixed(accuracy));
                break;
              }
              case 'floor': {
                const accuracy = setParam.accuracy ?? 0;
                newValue = Number(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}));
                newValue = Number((Math.floor((newValue ?? 0) * Math.pow(10, accuracy)) / Math.pow(10, accuracy)).toFixed(accuracy));
                break;
              }
              case 'concat': {
                // take two variables (or initialize them with 0), then divide the first value by the second
                let val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? '';
                let val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? '';
                if (typeof val1 === 'object') {
                  val1 = JSON.stringify(val1);
                }
                if (typeof val2 === 'object') {
                  val2 = JSON.stringify(val2);
                }
                newValue = val1 + val2;
                break;
              }
              case 'replace': {
                const input = String(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? '');
                const searchValue = setParam.searchValue ?? '';
                const replacementValue = setParam.replacementValue ?? '';

                // An empty search string is treated as a no-op to avoid surprising insertions.
                newValue = searchValue === '' ? input : input.split(searchValue).join(replacementValue);
                break;
              }
              case 'slice': {
                const input = String(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? '');
                newValue = input.slice(setParam.sliceStart ?? 0, setParam.sliceEnd);
                break;
              }
              case 'trim': {
                const input = String(getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? '');
                newValue = input.trim();
                break;
              }
              case 'push': {
                // take two variables (or initialize them with 0), then divide the first value by the second
                let val1 = getKeyVal(setParam.inputKey1, pipelineConfig.state?.[setParam.inputGroup1] ?? {}) ?? [];
                const val2 = getKeyVal(setParam.inputKey2, pipelineConfig.state?.[setParam.inputGroup2] ?? {}) ?? '';
                if (!Array.isArray(val1)) {
                  val1 = [];
                }
                val1.push(val2);
                newValue = val1;
                break;
              }
            }

          }
          if (setParam.newValueType === 'string' && !newValue) {
            newValue = '';
            pipelineConfig.setState(setParam.targetKey, newValue, setParam.targetGroup, false, '', 'string');
          } else if (setParam.newValueType === 'function' && ['replace', 'slice', 'trim'].includes(String(setParam.newValue))) {
            pipelineConfig.setState(setParam.targetKey, String(newValue ?? ''), setParam.targetGroup, false, '', 'string');
          } else if (['function', 'number'].includes(setParam.newValueType) && setParam.newValue !== 'push') {
            pipelineConfig.setState(setParam.targetKey, newValue, setParam.targetGroup, false, '', 'number');
          } else if (setParam.newValueType === 'boolean') {
            pipelineConfig.setState(setParam.targetKey, (newValue !== 'false' && newValue !== false), setParam.targetGroup, false);
          } else if (setParam.newValueType === 'stringList') {
            newValue = String(setParam.newValue);
            // use JSON.pare to convert the input string list of structure '["a", "b", "c"]' to JSON array ["a", "b", "c"]
            pipelineConfig.setState(setParam.targetKey, JSON.parse(newValue), setParam.targetGroup, false);
          } else {
            pipelineConfig.setState(setParam.targetKey, newValue, setParam.targetGroup);
          }
          // pipelineConfig.state[setParam.targetGroup][setParam.targetKey] = newValue;
          // setKeyVal(pipelineConfig.state, `${setParam.targetGroup}.${setParam.targetKey}`, newValue);
        }
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * check the internet connection by pinging a given server (google, by default)
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  checkInternetConnection = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject) => {
      const url = pipelineConfig.stageConfig?.pingURL ?? 'https://google.com';
      try {
        await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          cache: 'no-cache',
          referrerPolicy: 'no-referrer'
        });
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return;
      }
      catch (err) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoInternet, 'NoInternet', pipelineConfig.stage);
        return;
      }
    });
  }

  /**
   * Copy values within the value storage object
   * Requires an origin group- and key, as well as a target group- and key for each operation
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  copyValues = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const copyParams = pipelineConfig.stageConfig?.copyParameters;
      if (copyParams) {
        for (const copyParam of copyParams) {
          if (!pipelineConfig.state[copyParam.targetGroup]) {
            pipelineConfig.state[copyParam.targetGroup] = {};
          }
          if (copyParam.originKey === '*') {
            setKeyVal(
              pipelineConfig.state,
              `${copyParam.targetGroup}.${copyParam.targetKey}`,
              pipelineConfig.state?.[copyParam.originGroup] ?? {}
            );
            // pipelineConfig.state[copyParam.targetGroup][copyParam.targetKey] = pipelineConfig.state?.[copyParam.originGroup] ?? {};
          } else {
            setKeyVal(
              pipelineConfig.state,
              `${copyParam.targetGroup}.${copyParam.targetKey}`,
              getKeyVal(copyParam.originKey, pipelineConfig.state?.[copyParam.originGroup] ?? {})
            );
          }
        }
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /**
   * check whether a local value is equal to a given value (string-representation-equality)
   * choose a stage exit depending on the result
   * TODO: Add more checks than just equality
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  checkIf = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const localGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.functionParameters?.group] ?? {};
      const localVal = getKeyVal(pipelineConfig?.stageConfig?.functionParameters?.key, localGroup);
      let targetVal = pipelineConfig?.stageConfig?.functionParameters?.value;
      try {
        const targetValSplit = targetVal.split('.');
        const targetValGroup = targetValSplit[0];
        const targetValValue = targetValSplit.slice(1).join('.');
        if (targetValGroup && targetValValue) {
          targetVal = getKeyVal(targetValValue, pipelineConfig.state?.[targetValGroup] ?? {});
          if (targetVal === undefined || targetVal === null) {
            targetVal = pipelineConfig?.stageConfig?.functionParameters?.value;
          }
        }
      } catch (e) {
      }
      const condition = pipelineConfig?.stageConfig.functionParameters?.condition;
      switch (targetVal) {
        case '$notEmpty': {
          if (localVal !== undefined && localVal !== '') {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onTrue, 'onTrue', pipelineConfig.stage);
          } else {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFalse, 'onFalse', pipelineConfig.stage);
          }
          break;
        }
        default: {
          if (localVal === undefined) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFalse, 'onFalse', pipelineConfig.stage);
            break;
          }
          if (condition === '=') {
            if (localVal.toString().toLowerCase() === (targetVal ?? '').toString().toLowerCase()) {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onTrue, 'onTrue', pipelineConfig.stage);
            } else {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFalse, 'onFalse', pipelineConfig.stage);
            }
          } else {
            const num1 = Number(localVal);
            const num2 = Number(targetVal);
            if (isNaN(num1) || isNaN(num2)) {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onBadInput, 'onBadInput', pipelineConfig.stage);
            } else {
              let isTrue = false;
              switch (condition) {
                case '<': isTrue = num1 < num2; break;
                case '<=': isTrue = num1 <= num2; break;
                case '>=': isTrue = num1 >= num2; break;
                case '>': isTrue = num1 > num2; break;
              }
              if (isTrue) {
                pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onTrue, 'onTrue', pipelineConfig.stage);
              } else {
                pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFalse, 'onFalse', pipelineConfig.stage);
              }
            }
          }
          break;
        }
      }
      return of(true);
    });
  }

  checkObjectKeys = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const localGroup = pipelineConfig.state?.[pipelineConfig?.stageConfig?.functionParameters?.group] ?? {};
      const localVal = getKeyVal(pipelineConfig?.stageConfig?.functionParameters?.key, localGroup);
      const keysToCheck: string = pipelineConfig?.stageConfig?.functionParameters?.keys ?? '';
      const keysArray = keysToCheck.split(',').map(k => k.trim()).filter(k => k !== '');
      let allKeysPresent = true;

      if (typeof localVal !== 'object' || localVal === null) {
        allKeysPresent = false;
      } else {
        for (const key of keysArray) {
          if (!(key in localVal)) {
            allKeysPresent = false;
            break;
          }
        }
      }

      if (allKeysPresent) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onTrue, 'onTrue', pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFalse, 'onFalse', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  switchFunc = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject) => {
      const resKey = pipelineConfig?.stageConfig?.resKey;
      const resGroup = pipelineConfig?.stageConfig?.resGroup;
      const localGroup = pipelineConfig.state?.[resGroup] ?? {};
      const localVal = getKeyVal(resKey, localGroup);

      const switchParams = pipelineConfig.stageConfig?.switchParameters;
      if (switchParams && localVal !== undefined) {
        for (const switchParam of switchParams) {
          if (switchParam.switchVal.toString() === localVal.toString()) {
            pipelineConfig.executeStage(switchParam.switchTarget, 'onMatch', pipelineConfig.stage);
            return of(true);
          }
        }
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
      return of(true);
    });
  }


  /**
   * Initialize the QR-scanner function of the mobile device
   * extract the last four letters of the result, and use it as a temporary scanner serial (until verified)
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  scanQRScanner = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject): Observable<any> => {
      return of({}).pipe(
        switchMap(async () => {
          const barcodeData = await this.barcodeScanner.scan({
            preferFrontCamera: false,
            showFlipCameraButton: true,
            formats: 'QR_CODE',
            resultDisplayDuration: 0,
            prompt: pipelineConfig.translate('SETUP_SCAN_PROMPT'),
          });
          if (barcodeData.cancelled) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onCancelled, 'onCancelled', pipelineConfig.stage);
          } else {
            const serial = barcodeData.text.split('=').slice(-1)[0];
            // pipelineConfig.setState('serial', barcodeData.text.slice(-4), 'temp');
            pipelineConfig.setState('serial', serial, 'temp');
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
          return true;
        })
      );
    });
  }

  scanQRGeneric = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject): Observable<any> => {
      return of({}).pipe(
        switchMap(async () => {
          if (!((pipelineConfig.state.persistentValues.PLATFORM ?? '').includes('mobile'))) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onCancelled, 'onCancelled', pipelineConfig.stage);
            return true;
          }
          // TODO: make this customizable
          const barcodeData = await this.barcodeScanner.scan({
            preferFrontCamera: pipelineConfig.stageConfig.codeFrontCamera,
            showFlipCameraButton: pipelineConfig.stageConfig.codeEnableFlip,
            // preferFrontCamera: true,
            // showFlipCameraButton: false,
            formats: pipelineConfig.stageConfig.codeType ?? 'QR_CODE',
            resultDisplayDuration: 0,
            prompt: pipelineConfig.translate('CODE_SCAN_PROMPT'),
          });
          if (barcodeData.cancelled) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onCancelled, 'onCancelled', pipelineConfig.stage);
          } else {
            const resKey = pipelineConfig.stageConfig.resKey ?? '';
            const resGroup = pipelineConfig.stageConfig.resGroup ?? '';
            pipelineConfig.setState(resKey, barcodeData.text, resGroup);
            // const serial = barcodeData.text.split('=').slice(-1)[0];
            // pipelineConfig.setState('serial', barcodeData.text.slice(-4), 'temp');
            // pipelineConfig.setState('serial', serial, 'temp');
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          }
          return true;
        })
      );
    });
  }

  /**
   * attempt to fetch the Wi-Fi name of the current device from the server
   * store it as a persistent value
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  fetchWifiName = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((): Observable<boolean> => {
      return of({}).pipe(
        switchMap(async () => {
          // TODO: Fix on no WIFI
          // it currently just crashes to the home screen
          const { wifi, ambiguous } = await this.networkService.getAndSaveScannerWifi(pipelineConfig.state?.temp?.serial);
          if (ambiguous) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onAmbiguous, 'onAmbiguous', pipelineConfig.stage);
            return true;
          }
          pipelineConfig.setState(State.SCANNER_WIFI, wifi, 'persistentValues');
          pipelineConfig.setState(State.CONNECTION_TYPE, 'WIFI');
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return true;
        })
      );
    });
  }

  /**
   * attempt to connect to the scanner, and retrieve the proper serial and the scanner's status
   * check whether the scanner is currently allowed to be used
   * if all is well, store the scanner's serial as a permanent value
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  validateScanner = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject): Observable<any> => {
      return this.scanGenericService.connectToScanner(
        pipelineConfig.setState,
        pipelineConfig.setStep,
        // pipelineConfig.setBlockScreen,
        { serial: undefined },
        pipelineConfig.state.persistentValues,
        true,
      ).pipe(
        switchMap((r) => {
          if (r.status) {
            return this.scanGenericService.getStatus(
              pipelineConfig.scanner, pipelineConfig.state.persistentValues, pipelineConfig.setState, 5
            ).pipe(
              switchMap((state) => {
                // if ('ip' in state && 'serial' in state && 'host' in state && state.ip && state.serial && state.host) {
                if ('ip' in state && 'serial' in state && state.ip && state.serial) {
                  if (this.apiBasicService.checkForValidProduct(state)) {
                    pipelineConfig.setState('SCANNER', state, 'temp');
                    if ('api_version' in state) {
                      pipelineConfig.setState(State.SCANNER_VERSION, state.api_version);
                    } else if ('serial_2' in state) {
                        pipelineConfig.setState(State.SCANNER_VERSION, 2);
                    } else {
                      pipelineConfig.setState(State.SCANNER_VERSION, 1);
                    }
                    localStorage.setItem('serial', state.serial);
                    pipelineConfig.setStep({ scanner: state });
                    pipelineConfig.setState(State.SCANNER_SERIAL, state.serial, 'persistentValues');
                    pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
                    return of(true);
                  }
                  pipelineConfig.setState(State.CONNECTION_TYPE, '');
                  pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onErrorProduct, 'ProductError', pipelineConfig.stage);
                  return of(false);
                }
                pipelineConfig.setState(State.CONNECTION_TYPE, '');
                pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'Error', pipelineConfig.stage);
                return of(false);
              })
            );
          } else {
            pipelineConfig.setState(State.CONNECTION_TYPE, '');
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'Error', pipelineConfig.stage);
            return of(false);
          }
        }
        ));
    });
  }

  /**
   * check whether the scanner's firmware satisfies the current version requirements
   * if not, attempt to update the scanner
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  checkFirmware = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      // const scanner: ScannerStatus = pipelineConfig.state?.temp?.SCANNER ?? {};
      // pipelineConfig.setStep({ scanner });
      // pipelineConfig.setState(State.SCANNER_SERIAL, scanner.serial, 'persistentValues');
      return this.scannerApiService.checkScannerFirmwareOffline(
        pipelineConfig.state.persistentValues, pipelineConfig.config
      ).pipe(
        // switchMap((status: string) => {
        //   if (status && (status.startsWith('firmware') || status.startsWith('scanner_firmware'))) {
        //     pipelineConfig.setState('updateNeeded', true, 'temp');
        //     pipelineConfig.setState('firmware', status, 'temp');
        //     return of(false);
        //   } else {
        //     pipelineConfig.setStep({ scanner });
        //     pipelineConfig.setState(State.SCANNER_SERIAL, scanner.serial, 'persistentValues');
        //     return of(true);
        //   }
        // }),
        switchMap(async (status: string) => {
          // if (isValid) {
            // await this.networkService.disconnectScanner(pipelineConfig.state.persistentValues);
          await this.scannerApiService.disconnectScanner(pipelineConfig.state.persistentValues);
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return true;
          // } else {
          //   pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onUpdateNeeded, 'onUpdateNeeded', pipelineConfig.stage);
          //   return true;
          // }
        })
      );
    });
  }

  /**
   * #TODO
   * @param pipelineConfig bundle object containing all meta-parameters and getter/setter functions for the measurement
   */
  // updateFirmware = (pipelineConfig: PipelineStageConfig) => {
  //   return switchMap((_stati: StatusObject): Observable<boolean> => {
  //     const firmware = pipelineConfig.state?.temp?.firmware ?? '';
  //     // TODO: Add the online version
  //     return this.scannerApiService.updateScannerFirmwareOffline(firmware, pipelineConfig.state.persistentValues).pipe(
  //       timeout(45000), // Timeout after 45 seconds
  //       delay(2000),
  //       switchMap(async (status) => {
  //         pipelineConfig.setState(State.CONNECTION_TYPE, '');
  //         await this.networkService.disconnectScanner(pipelineConfig.state.persistentValues);
  //         pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
  //         return true;
  //       }),
  //       catchError(async (err) => {
  //         pipelineConfig.setState(State.CONNECTION_TYPE, '');
  //         await this.networkService.disconnectScanner(pipelineConfig.state.persistentValues);
  //         pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
  //         return true;
  //       })
  //     );
  //   });
  // }

  updateFirmware = (pipelineConfig: PipelineStageConfig, type: string, bootloader: boolean = false, downgrade = false) => {
    return switchMap((_stati: StatusObject): Observable<void> => {
      let method: OperatorFunction<any, any>;
      switch (type) {
        case 'espressif': method = this.scannerApiService.updateESP(pipelineConfig, downgrade); break;
        case 'nuvoton': method = this.scannerApiService.updateNuvoton(pipelineConfig); break;
        case 'infineon': method = this.scannerApiService.updateInfineon(pipelineConfig, bootloader); break;
        default: {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
          return;
        }
      }
      return of({}).pipe(
        method,
        switchMap(async (status) => {
          if (status.started) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return;
          }
          switch (status.target) {
            case 'onNotNeeded': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
              return;
            }
            case 'onNoWifi': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoWifi, 'onNoWifi', pipelineConfig.stage);
              return;
            }
            case 'onError': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
              return;
            }
            default: {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
              return;
            }
          }
        })
      );
    });
  }

  updateESP = (pipelineConfig: PipelineStageConfig) => {
    return switchMap((_stati: StatusObject): Observable<void> => {
      return of({}).pipe(
        this.scannerApiService.updateESP(pipelineConfig),
        switchMap(async (status) => {
          if (status.started) {
            pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
            return;
          }
          switch (status.target) {
            case 'onNotNeeded': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNotNeeded, 'onNotNeeded', pipelineConfig.stage);
              return;
            }
            case 'onNoWifi': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onNoWifi, 'onNoWifi', pipelineConfig.stage);
              return;
            }
            case 'onError': {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
              return;
            }
            default: {
              pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
              return;
            }
          }
        })
      );
    });
  }

  genResultURL = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return tap(() => {
      const step = pipelineConfig.getStep();
      const res = pipelineConfig.state.temp.result;
      const resultOnly = pipelineConfig.stageConfig?.partialResult ?? false;
      const resultBaseUrl = pipelineConfig.stageConfig?.resultBaseUrl?.trim() || undefined;
      if (res) {
        const url = getResult(
          res,
          step.serial,
          pipelineConfig.config,
          pipelineConfig.getState(State.SELECTED_LANGUAGE),
          resultOnly,
          resultBaseUrl,
        );
        const resKey = pipelineConfig.stageConfig.resKey ?? '';
        const resGroup = pipelineConfig.stageConfig.resGroup ?? '';
        pipelineConfig.setState(resKey, url, resGroup);
        // pipelineConfig.iab.create(url, '_system');
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return;
      }
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingResult, 'onMissingResult', pipelineConfig.stage);
    });
  }

  genResultTable = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((_stati: StatusObject) => {
      const dataAox: AoxMeasurementResult[] = pipelineConfig.state.persistentValues.AOX_MEASUREMENT_RESULTS;
      const dataPrecision: AoxMeasurementResult | PreciseAoxMeasurementResult = pipelineConfig.state.temp.result;
      const settings = pipelineConfig.stageConfig.resultTableParameters;
      const resGroup = pipelineConfig.stageConfig.resGroup;
      const resKey = pipelineConfig.stageConfig.resKey;

      const resultDict: any = {};
      for (const entry of settings) {
        switch (entry.type) {
          case 'aox': {
            if (!resultDict.aox) {
              resultDict.aox = {};
            }
            resultDict.aox[entry.caption] = [];
            for (const el of dataAox) {
              const val = getKeyVal(entry.key, el);
              resultDict.aox[entry.caption].push(val === undefined ? '' : val);
            }
            break;
          }
          case 'precise': {
            if (!resultDict.aox_precise) {
              resultDict.aox_precise = {};
            }
            const val = getKeyVal(entry.key, dataPrecision);
            resultDict.aox_precise[entry.caption] = val === undefined ? '' : val;
            break;
          }
          default: {
            continue;
          }
        }
      }
      pipelineConfig.setState(resKey, resultDict, resGroup);

      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  parseJSON = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      const getKey = pipelineConfig.stageConfig.getKey ?? '';
      const getGroup = pipelineConfig.stageConfig.getGroup ?? '';
      const resKey = pipelineConfig.stageConfig.resKey ?? '';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? '';
      if (resKey === '' || resGroup === '' || getKey === '' || getGroup === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
      const jsonString = getKeyVal(getKey, pipelineConfig.state?.[getGroup] ?? {});
      try {
        const json = JSON.parse(jsonString);
        pipelineConfig.setState(resKey, json, resGroup);
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
        return of(true);
      } catch (e) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
    });
  }

  hashString = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap(async (_stati: StatusObject) => {
      const getKey = pipelineConfig.stageConfig.getKey ?? '';
      const getGroup = pipelineConfig.stageConfig.getGroup ?? '';
      const resKey = pipelineConfig.stageConfig.resKey ?? '';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? '';
      const additionalArgument = pipelineConfig.stageConfig.additionalArgument ?? '';
      if (resKey === '' || resGroup === '' || getKey === '' || getGroup === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
      const inputString = getKeyVal(getKey, pipelineConfig.state?.[getGroup] ?? {}) ?? '';

      const escaped = additionalArgument.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

      // Create regex to match only allowed characters
      const regex = new RegExp(`[^a-zA-Z0-9${escaped}]`, 'g');

      // Clean the input string
      const cleansed: string = inputString.replace(regex, '').toUpperCase();

      // Encode to UTF-8 and hash with SHA-256
      const encoder = new TextEncoder();
      const data = encoder.encode(cleansed);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);


      // Convert hash to hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      pipelineConfig.setState(resKey, hashHex, resGroup);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }


  print = (
    pipelineConfig: PipelineStageConfig
  ) => {
    return switchMap((_stati: StatusObject): Observable<boolean> => {
      return of({}).pipe(
        switchMap(async () => {
          window.print();
          await new Promise((r) => setTimeout(r, 1000));
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
          return true;
        })
      );
    });
  }

  deleteSerial = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      localStorage.removeItem('serial');
      delete pipelineConfig.state.persistentValues.CONNECTION_TYPE;
      delete pipelineConfig.state.persistentValues.SCANNER_SERIAL;
      delete pipelineConfig.state.persistentValues.SCANNER_VERSION;
      delete pipelineConfig.state.persistentValues.SCANNER_STATUS;
      delete pipelineConfig.state.persistentValues.SCANNER_KEY;
      delete pipelineConfig.state.persistentValues.SCANNER_WIFI;
      pipelineConfig.setState(State.USB_MODE, false);
      pipelineConfig.setState(State.DEMO_MODE, false);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  validateCredits = (pipelineConfig: PipelineStageConfig) => {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      const type: MeasurementType = (pipelineConfig.stageConfig.additionalArgument ?? '') as MeasurementType;
      if (validateMeasurementAllowance(type, pipelineConfig.state.persistentValues)) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      } else {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  openURL(pipelineConfig: PipelineStageConfig) {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      let url = pipelineConfig.stageConfig.getURL ?? '';
      const state = pipelineConfig.state;
      try {
        if (url === '') {
          pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onMissingURL, 'onMissingURL', pipelineConfig.stage);
          return of(true);
        }
        url = fillURL(url, state);
        pipelineConfig.iab.create(url, '_system');
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      } catch (e) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }
    });
  }

  /*
   * Takes the Stored Information on the ScannerSerialNumber and returns the Software Version
   * uses the helper function getScannerSoftwareVersionBySerialNumber
   */
  checkScannerSoftwareVersion(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {

      const resKey = pipelineConfig.stageConfig.resKey ?? '';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? '';

      if (resKey === '' || resGroup === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }

      const scannerSerialNumber: string = pipelineConfig.state.persistentValues.SCANNER_SERIAL ?? '';
      let scannerSoftwareVersion = getScannerSoftwareVersionBySerialNumber(scannerSerialNumber);

      if (scannerSoftwareVersion === 'R5|R6|R8') {
        const scannerWifiFirmware: string = pipelineConfig.state.persistentValues.SCANNER_STATUS?.wifi_firmware ?? '';
        scannerSoftwareVersion = getScannerSoftwareVersionByWifiFirmware(scannerWifiFirmware);
      }

      pipelineConfig.setState(resKey, scannerSoftwareVersion, resGroup);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  /*
   * Function that sends a command via the electronUsbApiService to the scanner
   */
  sendGenericCommand(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {
      console.log('Sending Generic Command');

      const command = pipelineConfig.stageConfig.commandParameters.commandName ?? '';
      const transferConstant = pipelineConfig.stageConfig.commandParameters.transferConstant ?? '';

      const fromResKey = pipelineConfig.stageConfig.commandParameters.fromResKey ?? '';
      const fromResGroup = pipelineConfig.stageConfig.commandParameters.fromResGroup ?? '';

      const toResKey = pipelineConfig.stageConfig.commandParameters.toResKey ?? '';
      const toResGroup = pipelineConfig.stageConfig.commandParameters.toResGroup ?? '';

      let transferVariable: any = null;

      if (fromResKey !== '' && fromResGroup !== '') {
        transferVariable = getKeyVal(fromResKey, pipelineConfig.state?.[fromResGroup]);
      }

      const transferData = {transferConstant, transferVariable};

      if (command === '') {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
        return of(true);
      }

      try {
        const result = await this.scannerApiService.executeGenericCommand(
          pipelineConfig.state.persistentValues,
          command,
          transferData,
        );

        if (toResKey !== '' && toResGroup !== '') {
          pipelineConfig.setState(toResKey, result, toResGroup);
        }

        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      } catch (error) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onError, 'onError', pipelineConfig.stage);
      }
      return of(true);
    });
  }

  getMostRelevantMeasurementErrors(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {

      const errorThreshold = pipelineConfig?.state?.persistentValues?.measurementErrorThreshold ?? 0.2;

      const resKey = pipelineConfig.stageConfig.resKey ?? 'mostRelevantMeasurementErrors';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? 'temp';

      const measurementErrors = pipelineConfig?.state?.temp?.resultAox?.measurement_errors ?? {};

      if (Object.keys(measurementErrors).length === 0) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }

      const sortedRelevantErrors = Object.entries(measurementErrors)
        .map(([name, value]) => {
          const error = typeof value === 'number' ? value : (value as { error?: number })?.error;
          return { name, error };
        })
        .filter((error): error is { name: string; error: number } => typeof error.error === 'number' && error.error > errorThreshold)
        .sort((a, b) => b.error - a.error)
        .reduce((errors, error, index) => {
          const errorIndex = index + 1;
          errors[`error${errorIndex}`] = error.name;
          errors[`error${errorIndex}Value`] = error.error;
          return errors;
        }, {} as { [name: string]: string | number });

      pipelineConfig.setState(resKey, sortedRelevantErrors, resGroup);
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }

  getMostRelevantSeriesMeasurementErrors(pipelineConfig: PipelineStageConfig): OperatorFunction<StatusObject, Observable<any>> {
    // tslint:disable-next-line:variable-name
    return switchMap(async (_stati: StatusObject): Promise<Observable<any>> => {

      const errorThreshold = pipelineConfig?.state?.persistentValues?.measurementErrorThreshold ?? 0.2;

      const resKey = pipelineConfig.stageConfig.resKey ?? 'mostRelevantMeasurementErrors';
      const resGroup = pipelineConfig.stageConfig.resGroup ?? 'temp';
      const thresholdHistoryResKey = pipelineConfig.stageConfig.thresholdHistoryResKey ?? 'measurementErrorThresholdHistory';
      const thresholdHistoryResGroup = pipelineConfig.stageConfig.thresholdHistoryResGroup ?? resGroup;
      const sortedRelevantThresholdHistoryResKey =
        pipelineConfig.stageConfig.sortedRelevantThresholdHistoryResKey ?? 'sortedRelevantThresholdHistory';
      const thresholdHistoryRemappedResKey =
        pipelineConfig.stageConfig.thresholdHistoryRemappedResKey ?? 'thresholdHistoryRemapped';

      let seriesErrors = pipelineConfig?.state?.temp?.result?.series_errors ?? [];

      if (Array.isArray(seriesErrors) && seriesErrors.length === 0) {
        const measurementErrors = pipelineConfig?.state?.temp?.resultAox?.measurement_errors ?? {};
        seriesErrors = Object.keys(measurementErrors).length > 0 ? [measurementErrors] : [];
      }

      if (!Array.isArray(seriesErrors) || seriesErrors.length === 0) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }

      const thresholdHistory = seriesErrors.reduce((history, measurementErrors, index) => {
        Object.entries(measurementErrors ?? {}).forEach(([name, value]) => {
          if (name === 'total_error') {
            return;
          }
          if (!history[name]) {
            history[name] = Array(index).fill(null);
          }
          const error = typeof value === 'number' ? value : (value as { error?: number })?.error;
          history[name].push(typeof error === 'number' ? error > errorThreshold : false);
        });

        Object.keys(history)
          .filter((name) => !(measurementErrors && Object.prototype.hasOwnProperty.call(measurementErrors, name)))
          .forEach((name) => history[name].push(null));

        return history;
      }, {} as { [name: string]: Array<boolean | null> });

      const errorSumsByName = seriesErrors.reduce((errors, measurementErrors) => {
        Object.entries(measurementErrors ?? {}).forEach(([name, value]) => {
          if (name === 'total_error') {
            return;
          }
          const error = typeof value === 'number' ? value : (value as { error?: number })?.error;
          if (typeof error !== 'number' || error <= 0) {
            return;
          }
          if (!errors[name]) {
            errors[name] = 0;
          }
          errors[name] += error;
        });
        return errors;
      }, {} as { [name: string]: number });

      if (Object.keys(errorSumsByName).length === 0 && Object.keys(thresholdHistory).length === 0) {
        pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onFail, 'onFail', pipelineConfig.stage);
        return of(true);
      }

      const averagedErrorsByName = (Object.entries(errorSumsByName) as [string, number][])
        .reduce((errors, [name, sum]) => {
          errors[name] = sum / seriesErrors.length;
          return errors;
        }, {} as { [name: string]: number });

      const relevantErrorNames = new Set((Object.entries(thresholdHistory) as [string, Array<boolean | null>][])
        .filter(([, history]) => history.some((errorAboveThreshold) => errorAboveThreshold === true))
        .map(([name]) => name));

      const sortedRelevantErrorEntries = (Object.entries(averagedErrorsByName) as [string, number][])
        .filter(([name]) => relevantErrorNames.has(name))
        .sort(([, errorA], [, errorB]) => errorB - errorA);

      const sortedRelevantErrors = sortedRelevantErrorEntries
        .reduce((errors, [name, error], index) => {
          const errorIndex = index + 1;
          errors[`error${errorIndex}`] = name;
          errors[`error${errorIndex}Value`] = error;
          return errors;
        }, {} as { [name: string]: string | number });

      const sortedRelevantThresholdHistory = sortedRelevantErrorEntries
        .reduce((history, [name]) => {
          history[name] = thresholdHistory[name] ?? [];
          return history;
        }, {} as { [name: string]: Array<boolean | null> });

      pipelineConfig.setState(thresholdHistoryResKey, thresholdHistory, thresholdHistoryResGroup);
      pipelineConfig.setState(sortedRelevantThresholdHistoryResKey, sortedRelevantThresholdHistory, thresholdHistoryResGroup);

      if (seriesErrors.length > 0) {
        const thresholdHistoryRemapped = Object.values(sortedRelevantThresholdHistory)
          .reduce((history, values, errorIndex) => {
            values.forEach((value, valueIndex) => {
              history[`error${errorIndex + 1}_${valueIndex}`] = value;
            });
            return history;
          }, {} as { [name: string]: boolean | null });
        pipelineConfig.setState(thresholdHistoryRemappedResKey, thresholdHistoryRemapped, thresholdHistoryResGroup);
      }

      pipelineConfig.setState(resKey, sortedRelevantErrors, resGroup);
      pipelineConfig.setState('noRelevantErrors', sortedRelevantErrorEntries.length === 0, 'temp');
      pipelineConfig.executeStage(pipelineConfig?.stageConfig?.eventTargets?.onSuccess, 'onSuccess', pipelineConfig.stage);
      return of(true);
    });
  }
}
